import React, { useCallback, useMemo, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useIsLoggedIn } from '@order/AuthMachine';
import {
  LoadingAnimation,
  NotFoundView,
  RefreshErrorState,
  SweetpassFreeTrialNotEligible,
  SweetpassSignedOut,
} from '@order/components';
import type { SubscriptionCreditCard } from '@order/graphql';
import {
  TierName,
  useGetPaymentSubscriptionProfileQuery,
} from '@order/graphql';
import { useSweetpassPlan, useSweetpassTier } from '@order/hooks';

import { TierUpgradeCelebrationModal } from '../../components/TierUpgradeCelebrationModal';
import { useNavigateToMainTabs } from '../../navigation';
import type { SweetpassCheckoutScreenProps } from '../../navigation/AppNavigation.props';
import {
  useSubscribeToSweetpassPlus,
  useSweetpassCheckoutContentfulData,
} from './hooks';
import { SweetpassCheckoutScreenContent } from './SweetpassCheckoutScreenContent';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutScreen = (
  props: SweetpassCheckoutScreenProps,
) => {
  const { navigation, route } = props;

  // ─── Navigation Params ───────────────────────────────────────────────

  const {
    planId: planIdFromParams,
    campaignId,
    redirectToAfterUpgrade,
    redirectToOrderId,
  } = route.params ?? {};

  const shouldRedirectToOrderStatusAfterUpgrade =
    redirectToAfterUpgrade === 'order-status' &&
    redirectToOrderId !== undefined;

  // ─── Context ─────────────────────────────────────────────────────────

  const navigateToMainTabs = useNavigateToMainTabs();

  // ─── State ───────────────────────────────────────────────────────────

  const [hasCustomerBeenUpgraded, setHasCustomerBeenUpgraded] = useState(false);
  const [selectedCreditCard, selectCreditCard] =
    useState<Partial<SubscriptionCreditCard>>();

  // ─── Queries ─────────────────────────────────────────────────────────

  const {
    isSweetpassTierLoading,
    sweetpassTierName,
    trialDuration,
    trialDurationSingular,
    isEligibleForFreeTrial,
    hasUnknownTierError,
  } = useSweetpassTier('cache-and-network');

  const {
    planId,
    planPrice,
    isLoadingPlan,
    hasFreeTrial,
    billingFrequencyUnit,
    hasUnknownPlanError,
  } = useSweetpassPlan(planIdFromParams);

  const [paymentSubscriptionProfileResponse] =
    useGetPaymentSubscriptionProfileQuery({
      requestPolicy: 'cache-and-network',
    });

  // ─── Contentful Data ─────────────────────────────────────────────────

  const { content: contentfulData, isLoading: isContentLoading } =
    useSweetpassCheckoutContentfulData({
      planPrice,
      trialDuration,
      trialDurationSingular,
      billingFrequencyUnit,
    });

  // ─── Derived Data ────────────────────────────────────────────────────

  const isSweetpassPlusMember = sweetpassTierName === TierName.SweetpassPlus;

  const paymentSubscriptionProfile =
    paymentSubscriptionProfileResponse.data?.paymentSubscriptionProfile;

  const creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>> =
    paymentSubscriptionProfile?.creditCards ?? [];

  const title = hasFreeTrial
    ? contentfulData.freeTrialTitle
    : contentfulData.title;
  const termsAndConditions = hasFreeTrial
    ? contentfulData.freeTrialTermsAndConditions
    : contentfulData.termsAndConditions;
  const cta = hasFreeTrial ? contentfulData.freeTrialCta : contentfulData.cta;

  // ─── Flags ───────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();

  const hasLoadedTierData = !isSweetpassTierLoading;

  const isLoadingPaymentSubscriptionProfile =
    paymentSubscriptionProfileResponse.fetching;

  const isLoading =
    isSweetpassTierLoading ||
    isLoadingPlan ||
    isLoadingPaymentSubscriptionProfile ||
    isContentLoading;

  const isMissingPlan = !planId;
  const isMissingContentfulData = !contentfulData;
  const isMissingPaymentSubscriptionProfile = !paymentSubscriptionProfile;
  const isMissingRequiredData =
    isMissingPlan ||
    isMissingContentfulData ||
    isMissingPaymentSubscriptionProfile;

  const isNotEligible =
    !isSweetpassPlusMember && hasFreeTrial && !isEligibleForFreeTrial;

  const hasUnknownError = hasUnknownTierError || hasUnknownPlanError;

  // ─── Callbacks ───────────────────────────────────────────────────────

  const navigateAfterUpgrade = useCallback(() => {
    if (shouldRedirectToOrderStatusAfterUpgrade) {
      // NOTE: We use `replace` here because we don't want to keep the current screen
      //       in the navigation history since it should be unavailable after the upgrade.
      navigation.replace('OrderStatus', { orderId: redirectToOrderId });

      return;
    }

    void navigateToMainTabs('LoyaltyTab', {
      screen: 'SweetpassHome',
    });
  }, [
    navigation,
    redirectToOrderId,
    shouldRedirectToOrderStatusAfterUpgrade,
    navigateToMainTabs,
  ]);

  const navigateToHome = useCallback(() => {
    void navigateToMainTabs('HomeTab', {
      screen: 'Home',
    });
  }, [navigateToMainTabs]);

  // ─── Subscription Hook ───────────────────────────────────────────────

  const handleCta = useSubscribeToSweetpassPlus({
    planId: planId ?? '',
    paymentId: selectedCreditCard?.id,
    campaignId,
    redirectToAfterUpgrade,
    hasFreeTrial,
    setHasCustomerBeenUpgraded,
  });

  // ─── Redirects ───────────────────────────────────────────────────────

  // NOTE: Redirect conditions are presented as an array,
  //       making it easy to check several conditions.

  const redirectToSweetpassHomeScreenConditions =
    useMemo<ScreenRedirectConditions>(
      () => [
        !hasCustomerBeenUpgraded && hasLoadedTierData && isSweetpassPlusMember,
      ],
      [hasCustomerBeenUpgraded, hasLoadedTierData, isSweetpassPlusMember],
    );

  /**
   * Redirects customer to the Sweetpass Home screen, if any of the following conditions are met.
   *
   * - The current customer has Sweetpass+ subscription (already upgraded)
   */
  useFocusEffect(
    useCallback(() => {
      const shouldRedirect =
        redirectToSweetpassHomeScreenConditions.some(Boolean);

      if (!shouldRedirect) return;

      void navigateToMainTabs('LoyaltyTab', { screen: 'SweetpassHome' });
    }, [navigateToMainTabs, redirectToSweetpassHomeScreenConditions]),
  );

  /**
   * Redirects logged-out customers to the login, then to Sweetpass Upgrade screen.
   */
  const handlePressSignIn = useCallback(() => {
    navigation.replace('Auth', {
      screen: 'Login',
      params: { redirect: 'sweetpassUpgrade' },
    });
  }, [navigation]);

  const handlePressJoinNow = useCallback(() => {
    navigation.replace('Auth', {
      screen: 'Join',
      params: {
        redirect: 'sweetpassUpgrade',
        redirectToPlanId: planIdFromParams,
        redirectToCampaignId: campaignId,
      },
    });
  }, [navigation, planIdFromParams, campaignId]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return (
      <SweetpassSignedOut
        testID="sweetpass-signed-out"
        onPressSignIn={handlePressSignIn}
        onPressJoinNow={handlePressJoinNow}
      />
    );
  }

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (hasUnknownError) {
    return <RefreshErrorState />;
  }

  if (isMissingRequiredData) {
    return <NotFoundView />;
  }

  if (isNotEligible) {
    return <SweetpassFreeTrialNotEligible />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <SweetpassCheckoutScreenContent
        testID="sweetpass-checkout-screen"
        title={title}
        termsAndConditions={termsAndConditions}
        cta={cta}
        planPrice={planPrice}
        hasFreeTrial={hasFreeTrial}
        selectedCreditCard={selectedCreditCard}
        creditCards={creditCards}
        handleSelectCreditCard={selectCreditCard}
        ctaHandler={handleCta}
      />

      <TierUpgradeCelebrationModal
        testID="sweetpass-checkout-celebration-modal"
        onDismiss={navigateToHome}
        onExplore={navigateAfterUpgrade}
        visible={hasCustomerBeenUpgraded}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ScreenRedirectConditions = readonly boolean[];
